/**
 * ModuleCanvas — 12-col × 140px grid canvas for V2 workspace modules.
 *
 * Cycle 101 sprint-3 Task 3.1 (sprint.md:305, SDD §5.1 line 349).
 *
 * Naming: the SDD §5.1 table calls the V2 deliverable `WorkspaceCanvas.tsx`,
 * but that filename is occupied by the V1 shell-tile canvas. V2 ships here
 * as `ModuleCanvas` (the name the sprint-2 V2 shell placeholder already
 * used at `InvestigationWorkspacePageV2.tsx`). The V1 `WorkspaceCanvas.tsx`
 * retires in the sprint-3 tail V1-retirement bundle; no rename needed.
 *
 * Contract:
 *   * CSS Grid: 12 cols (`repeat(12, 1fr)`), 140px row unit.
 *   * Each `ModulePlacement` renders at `grid-column: x+1 / span w` +
 *     `grid-row: y+1 / span h` (CSS Grid lines are 1-indexed; our
 *     placement coords are 0-indexed).
 *   * Overlap allowed: `z-index` per placement (defaults to 0). Drag-start
 *     bumps the dragged placement to `maxZ + 1` so it surfaces above
 *     siblings and stays on top after release.
 *   * Snap-drag via Pointer Events API. Delta is rounded to the nearest
 *     grid cell; moves clamp to `x ∈ [0, 12-w]`, `y >= 0`; resizes clamp to
 *     `w ∈ [1, 12-x]`, `h >= 1`.
 *   * SE-corner resize handle; pointer-capture prevents drag drop on
 *     pointer-leave.
 *
 * A11y (sprint-3 AC-9):
 *   * Container carries `role="main"` + `aria-label` so screen readers can
 *     traverse canvas structure.
 *   * Each placement is a focusable `role="region"` with `tabIndex=0` +
 *     module-specific `aria-label`. DOM order is placement insertion order,
 *     matching grid left-right-top-bottom per §5.4.
 *
 * Out of scope for Task 3.1:
 *   * Mode-switch FLIP animation — Task 3.5 wraps this in `LayoutGroup`.
 *   * + FAB / add-module — Task 3.6 is a sibling component.
 *   * RESET PRESET modal — Task 3.7 is a sibling modal that calls
 *     `layout.resetToPreset()`.
 */

import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import type { ModulePlacement } from '../../lib/modePresets';
import {
  getModuleEntry,
  type ModuleRegistryEntry,
  type ModuleRenderContext,
} from '../../lib/moduleRegistry';
import type { UseModeCanvasLayoutResult } from '../../hooks/useModeCanvasLayout';

// ── Constants ──────────────────────────────────────────────────────────

export const GRID_COLS = 12;
export const ROW_PX = 140;

/** Minimum drag distance (px) before a pointer-down becomes a drag. */
const DRAG_THRESHOLD_PX = 4;

// ── Types ───────────────────────────────────────────────────────────────

export interface ModuleCanvasProps {
  /** The active `useModeCanvasLayout` result — canvas mirrors its state. */
  readonly layout: UseModeCanvasLayoutResult;
  /** Container id — ModesRow points aria-controls at this (sprint-2 AC-8). */
  readonly id?: string;
  /** Fires when the user focuses or clicks a placement. */
  readonly onPlacementFocus?: (placementId: string) => void;
  /** Additional class names for layout overrides. */
  readonly className?: string;
}

type DragKind = 'move' | 'resize-se';

interface DragState {
  readonly placementId: string;
  readonly kind: DragKind;
  readonly startPointerX: number;
  readonly startPointerY: number;
  readonly startX: number;
  readonly startY: number;
  readonly startW: number;
  readonly startH: number;
  /** Width of one grid column (px) at drag start. Computed from container rect. */
  readonly colPx: number;
}

// ── Pure helpers (exported for unit tests) ──────────────────────────────

export function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

export function maxZ(placements: readonly ModulePlacement[]): number {
  let m = 0;
  for (const p of placements) {
    if (p.z !== undefined && p.z > m) m = p.z;
  }
  return m;
}

export function computeMoveTarget(
  drag: Pick<
    DragState,
    'startPointerX' | 'startPointerY' | 'startX' | 'startY' | 'startW' | 'colPx'
  >,
  pointerX: number,
  pointerY: number
): { x: number; y: number } {
  const dx = pointerX - drag.startPointerX;
  const dy = pointerY - drag.startPointerY;
  const deltaCols = Math.round(dx / drag.colPx);
  const deltaRows = Math.round(dy / ROW_PX);
  const x = clamp(drag.startX + deltaCols, 0, GRID_COLS - drag.startW);
  const y = Math.max(0, drag.startY + deltaRows);
  return { x, y };
}

/**
 * Render a registry entry into a ReactNode.
 *
 * Sprint-204 S2.4 adapter (Q-S2.0-a follow-through): when `entry.render`
 * is defined (context-bearing canonical entries — sprint-204 S2.8/S2.9
 * adapters + sprint-3 S3.5 InvestigationDrawer wiring), invoke it with the
 * supplied `ctx`. Otherwise fall back to the code-split `<entry.component />`
 * path (the cycle-101 production pattern).
 *
 * Pure for testability. ModuleCanvas wraps the result in <Suspense> to
 * cover both paths (render fn returning a Promise-based subtree is
 * supported transparently).
 */
export function renderModuleEntry(
  entry: ModuleRegistryEntry,
  ctx?: ModuleRenderContext
): ReactNode {
  if (entry.render !== undefined) {
    return entry.render(ctx);
  }
  const Comp = entry.component;
  return <Comp />;
}

export function computeResizeTarget(
  drag: Pick<
    DragState,
    'startPointerX' | 'startPointerY' | 'startX' | 'startW' | 'startH' | 'colPx'
  >,
  pointerX: number,
  pointerY: number
): { w: number; h: number } {
  const dx = pointerX - drag.startPointerX;
  const dy = pointerY - drag.startPointerY;
  const deltaCols = Math.round(dx / drag.colPx);
  const deltaRows = Math.round(dy / ROW_PX);
  const w = clamp(drag.startW + deltaCols, 1, GRID_COLS - drag.startX);
  const h = Math.max(1, drag.startH + deltaRows);
  return { w, h };
}

// ── Component ───────────────────────────────────────────────────────────

export function ModuleCanvas({
  layout,
  id,
  onPlacementFocus,
  className,
}: ModuleCanvasProps) {
  const containerRef = useRef<HTMLElement | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);

  const { placements, updatePlacement } = layout;

  const placementsById = useMemo(() => {
    const m = new Map<string, ModulePlacement>();
    for (const p of placements) m.set(p.id, p);
    return m;
  }, [placements]);

  const handlePointerDown = useCallback(
    (
      e: ReactPointerEvent<HTMLDivElement>,
      placement: ModulePlacement,
      kind: DragKind
    ) => {
      if (e.button !== 0) return; // left-button only
      // Don't start a move drag from interactive children (buttons, links…)
      const target = e.target as HTMLElement;
      if (
        kind === 'move' &&
        target.closest('button, a, input, select, textarea, [role="button"]')
      ) {
        return;
      }

      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const colPx = rect.width / GRID_COLS;
      if (colPx <= 0) return; // zero-width container — no grid math possible

      // Bump z so dragged placement floats above siblings.
      const currentMaxZ = maxZ(placements);
      if ((placement.z ?? 0) < currentMaxZ + 1) {
        updatePlacement(placement.id, { z: currentMaxZ + 1 });
      }

      setDrag({
        placementId: placement.id,
        kind,
        startPointerX: e.clientX,
        startPointerY: e.clientY,
        startX: placement.x,
        startY: placement.y,
        startW: placement.w,
        startH: placement.h,
        colPx,
      });

      try {
        (e.currentTarget as Element).setPointerCapture(e.pointerId);
      } catch {
        // jsdom may not support pointer capture; ignore.
      }
      e.preventDefault();
    },
    [placements, updatePlacement]
  );

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!drag) return;
      const adx = Math.abs(e.clientX - drag.startPointerX);
      const ady = Math.abs(e.clientY - drag.startPointerY);
      if (adx < DRAG_THRESHOLD_PX && ady < DRAG_THRESHOLD_PX) return;

      const placement = placementsById.get(drag.placementId);
      if (!placement) return;

      if (drag.kind === 'move') {
        const { x, y } = computeMoveTarget(drag, e.clientX, e.clientY);
        if (x !== placement.x || y !== placement.y) {
          updatePlacement(drag.placementId, { x, y });
        }
      } else {
        const { w, h } = computeResizeTarget(drag, e.clientX, e.clientY);
        if (w !== placement.w || h !== placement.h) {
          updatePlacement(drag.placementId, { w, h });
        }
      }
    },
    [drag, placementsById, updatePlacement]
  );

  const handlePointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!drag) return;
      try {
        (e.currentTarget as Element).releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
      setDrag(null);
    },
    [drag]
  );

  // DISS-002-R2 fix: treat `pointercancel` (gesture arbitration, tab
  // switches, OS-level interruption) as a drag terminator. Without this,
  // `drag` could remain set after the browser silently aborts the
  // interaction, leaving the component in a stale "dragging" visual state
  // and inviting spurious move/resize updates when a later pointer event
  // arrives.
  const handlePointerCancel = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!drag) return;
      try {
        (e.currentTarget as Element).releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
      setDrag(null);
    },
    [drag]
  );

  // Clear drag on unmount in case a pointer capture never releases.
  useEffect(() => {
    return () => {
      setDrag(null);
    };
  }, []);

  return (
    <main
      ref={(el) => {
        containerRef.current = el;
      }}
      id={id}
      role="main"
      aria-label="Workspace module canvas"
      className={className}
      style={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)`,
        gridAutoRows: `${ROW_PX}px`,
        gap: 8,
        padding: 12,
        minHeight: '100%',
        background: 'var(--cd-bg-deep)',
        overflowY: 'auto',
      }}
    >
      {placements.map((p) => (
        <PlacementCell
          key={p.id}
          placement={p}
          isDragging={drag?.placementId === p.id}
          onPointerDownMove={(e) => handlePointerDown(e, p, 'move')}
          onPointerDownResize={(e) => handlePointerDown(e, p, 'resize-se')}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onFocus={() => onPlacementFocus?.(p.id)}
        />
      ))}
    </main>
  );
}

// ── Placement cell ──────────────────────────────────────────────────────

interface PlacementCellProps {
  readonly placement: ModulePlacement;
  readonly isDragging: boolean;
  readonly onPointerDownMove: (e: ReactPointerEvent<HTMLDivElement>) => void;
  readonly onPointerDownResize: (e: ReactPointerEvent<HTMLDivElement>) => void;
  readonly onPointerMove: (e: ReactPointerEvent<HTMLDivElement>) => void;
  readonly onPointerUp: (e: ReactPointerEvent<HTMLDivElement>) => void;
  readonly onPointerCancel: (e: ReactPointerEvent<HTMLDivElement>) => void;
  readonly onFocus: () => void;
}

function PlacementCell({
  placement: p,
  isDragging,
  onPointerDownMove,
  onPointerDownResize,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onFocus,
}: PlacementCellProps) {
  const entry = getModuleEntry(p.moduleId);
  const label = entry?.label ?? p.moduleId;

  return (
    <div
      data-testid={`placement-${p.id}`}
      data-module-id={p.moduleId}
      role="region"
      aria-label={`${label} module`}
      tabIndex={0}
      onPointerDown={onPointerDownMove}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onFocus={onFocus}
      style={{
        gridColumn: `${p.x + 1} / span ${p.w}`,
        gridRow: `${p.y + 1} / span ${p.h}`,
        zIndex: p.z ?? 0,
        position: 'relative',
        minHeight: 0,
        minWidth: 0,
        background: 'var(--cd-bg-card)',
        border: isDragging
          ? '1px solid var(--cd-sig-iris)'
          : '1px solid var(--cd-border)',
        borderRadius: 'var(--cd-radius)',
        boxShadow: isDragging ? 'var(--cd-shadow-2)' : 'none',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        touchAction: 'none',
        transition: isDragging ? 'none' : 'border-color .12s, box-shadow .12s',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {entry ? (
        <Suspense fallback={<ModuleLoadingFallback label={label} />}>
          {renderModuleEntry(entry)}
        </Suspense>
      ) : (
        <ModuleLoadingFallback label={`Unknown module: ${p.moduleId}`} />
      )}

      {/* SE-corner resize handle. */}
      <div
        data-testid={`resize-${p.id}`}
        role="button"
        aria-label={`Resize ${label}`}
        tabIndex={-1}
        onPointerDown={onPointerDownResize}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        style={{
          position: 'absolute',
          right: 2,
          bottom: 2,
          width: 12,
          height: 12,
          cursor: 'nwse-resize',
          background:
            'linear-gradient(135deg, transparent 40%, var(--cd-border-strong) 40% 60%, transparent 60%)',
          touchAction: 'none',
        }}
      />
    </div>
  );
}

function ModuleLoadingFallback({ label }: { label: string }): ReactNode {
  return (
    <div
      style={{
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--cd-font-mono)',
        fontSize: 10,
        color: 'var(--cd-fg-muted)',
        letterSpacing: '0.10em',
        textTransform: 'uppercase',
      }}
    >
      Loading {label}…
    </div>
  );
}
